// "Snow & sparkle" toggle of the public pages (front-end redesign). The festive snowfall
// and twinkling run only under `html.js:not(.calm)` (site.css); this button lets a visitor
// pause them, and the choice is remembered in localStorage. With prefers-reduced-motion
// the default is calm, and the CSS switches every animation off regardless.
export const CALM_KEY = 'xmas-calm';
// Structural types: this module is also type-checked and unit-tested without the DOM lib.
export interface CalmStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}
export interface FestiveButton {
  hidden: boolean | string;
  textContent: string | null;
  setAttribute(name: string, value: string): void;
  addEventListener(type: 'click', listener: () => void): void;
}
export interface FestiveDoc {
  getElementById(id: string): FestiveButton | null;
  readonly documentElement: { readonly classList: { toggle(token: string, force: boolean): boolean } };
}
/** Calm (no motion) when the visitor chose it, or by default under reduced motion. */
export function initialCalm(stored: string | null, reducedMotion: boolean): boolean {
  return stored === '1' || (stored === null && reducedMotion);
}
/** localStorage, or null where it is blocked (private mode, disabled storage). */
export function safeLocalStorage(): CalmStorage | null {
  try {
    const g = globalThis as { localStorage?: CalmStorage };
    return g.localStorage ?? null;
  } catch {
    return null;
  }
}
function read(storage: CalmStorage | null): string | null {
  try {
    return storage?.getItem(CALM_KEY) ?? null;
  } catch {
    return null;
  }
}
function write(storage: CalmStorage | null, calm: boolean): void {
  try {
    storage?.setItem(CALM_KEY, calm ? '1' : '0');
  } catch {
    // Not persisted; the toggle still works for this page view.
  }
}
/** Binds #festive-toggle (no-op when the page has none). */
export function initFestiveToggle(
  doc: FestiveDoc,
  storage: CalmStorage | null,
  reducedMotion: boolean,
): void {
  const button = doc.getElementById('festive-toggle');
  if (!button) return;
  let calm = initialCalm(read(storage), reducedMotion);
  const apply = (): void => {
    doc.documentElement.classList.toggle('calm', calm);
    button.setAttribute('aria-pressed', calm ? 'false' : 'true');
    button.textContent = calm ? 'Snow & sparkle: off' : 'Snow & sparkle: on';
  };
  apply();
  button.hidden = false;
  button.addEventListener('click', () => {
    calm = !calm;
    write(storage, calm);
    apply();
  });
}
